import React, { Component } from "react";
import CESSVG from "../../../Assets/svg/CESSVG.svg";
import {
  FetchChatSessionsAction,
  FetchIndividualChatSessionAction,
  ContinueChatSessionAddSessionIDToReduxAction,
  resetContinueChatSessionAction,
  DeleteChatSessionAction,
  ResetIsNewChatSession,
  RenameChatSessionAction,
} from "../../../Actions/AI";
import { connect } from "react-redux";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import PropTypes from "prop-types";
import {
  CollapseCX2SidebarInactiveAction,
  CollapseCX2SidebarActiveAction,
} from "../../../Actions/Interactions";
import { MoreHorizontal, Pencil, Trash2, Plus, User, PanelLeft } from "lucide-react";

class AIChatBotSideBar extends Component {
  static propTypes = {
    FetchChatSessionsAction: PropTypes.func.isRequired,
    FetchIndividualChatSessionAction: PropTypes.func.isRequired,
    ContinueChatSessionAddSessionIDToReduxAction: PropTypes.func.isRequired,
    SideBarchatSessions: PropTypes.array,
    SideBarchatSessionsFetchSuccess: PropTypes.bool,
    navigate: PropTypes.func.isRequired,
    resetContinueChatSessionAction: PropTypes.func.isRequired,
    ResetIsNewChatSession: PropTypes.func.isRequired,
    DeleteChatSessionAction: PropTypes.func.isRequired,
    RenameChatSessionAction: PropTypes.func.isRequired,
    user: PropTypes.object,
    toggleSidebar: PropTypes.func.isRequired,
    CollapseCX2SidebarInactiveAction: PropTypes.func.isRequired,
    CollapseCX2SidebarActiveAction: PropTypes.func.isRequired,
    isCollapsed: PropTypes.bool,
  };

  state = {
    activeMenuId: null,
    isMobileView: false,
    editingSessionId: null,
    editingTitle: "",
  };

  componentDidMount() {
    if (!this.props.SideBarchatSessionsFetchSuccess || !this.props.SideBarchatSessions) {
      this.props.FetchChatSessionsAction();
    }
    this.checkMobileView();
    this.debouncedCheckMobileView = this.debounce(this.checkMobileView, 250);
    window.addEventListener("resize", this.debouncedCheckMobileView);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.SideBarchatSessionsFetchSuccess && !this.props.SideBarchatSessionsFetchSuccess) {
      this.props.FetchChatSessionsAction();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.debouncedCheckMobileView);
    if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
  }

  debounce = (func, delay) => {
    return (...args) => {
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => func(...args), delay);
    };
  };

  checkMobileView = () => {
    const isMobile = window.innerWidth < 768;
    if (this.state.isMobileView !== isMobile) this.setState({ isMobileView: isMobile });
  };

  handleContinueChat = (sessionId) => {
    this.props.FetchIndividualChatSessionAction(sessionId);
    this.props.ContinueChatSessionAddSessionIDToReduxAction(sessionId);
    this.props.navigate(`/dashboard/ai-chatbot/${sessionId}`);
    this.props.ResetIsNewChatSession();
  };

  handleStartNewChat = () => {
    this.props.resetContinueChatSessionAction();
    this.props.navigate("/dashboard/ai-chatbot");
  };

  handleDeleteChatSession = (sessionId) => {
    const currentPathId = this.props.location?.pathname?.split("/").pop();
    this.props.DeleteChatSessionAction(sessionId).then(() => {
      this.props.FetchChatSessionsAction();
      this.setState({ activeMenuId: null });
      if (currentPathId === sessionId.toString()) {
        this.props.navigate("/dashboard/ai-chatbot");
      }
    });
  };

  toggleMenu = (sessionId) => {
    this.setState((prevState) => ({
      activeMenuId: prevState.activeMenuId === sessionId ? null : sessionId,
    }));
  };

  handleCollapseToggle = () => {
    if (this.state.isMobileView) {
      this.props.toggleSidebar();
    } else {
      if (this.props.isCollapsed) {
        this.props.CollapseCX2SidebarInactiveAction();
      } else {
        this.props.CollapseCX2SidebarActiveAction();
      }
    }
  };

  handleStartRename = (sessionId, currentTitle) => {
    this.setState({ editingSessionId: sessionId, editingTitle: currentTitle || "", activeMenuId: null });
  };

  handleRenameChange = (e) => {
    this.setState({ editingTitle: e.target.value });
  };

  handleRenameSubmit = (sessionId) => {
    this.props.RenameChatSessionAction(sessionId, this.state.editingTitle).then(() => {
      this.props.FetchChatSessionsAction();
      this.setState({ editingSessionId: null, editingTitle: "" });
    });
  };

  handleRenameCancel = () => {
    this.setState({ editingSessionId: null, editingTitle: "" });
  };

  handleRenameKeyDown = (e, sessionId) => {
    if (e.key === "Enter") this.handleRenameSubmit(sessionId);
    else if (e.key === "Escape") this.handleRenameCancel();
  };

  renderChatSessions() {
    const { SideBarchatSessions, SideBarchatSessionsFetchSuccess } = this.props;

    if (!SideBarchatSessionsFetchSuccess) {
      return <div className="text-sm text-gray-400 px-3 py-4">Loading...</div>;
    }

    if (!SideBarchatSessions || SideBarchatSessions.length === 0) {
      return (
        <div className="text-sm text-gray-400 px-3 py-4 text-center">
          No chat sessions yet
        </div>
      );
    }

    const sortedSessions = [...SideBarchatSessions].sort(
      (a, b) => new Date(b.started_at) - new Date(a.started_at)
    );

    return sortedSessions.map((session) => {
      const userMessages = (session.messages || []).filter((m) => m.role === "user");
      const lastUserMessage = userMessages.length > 0
        ? userMessages[userMessages.length - 1].content
        : "No messages yet";
      const trimmedMessage = lastUserMessage.length > 35
        ? lastUserMessage.substring(0, 35) + "..."
        : lastUserMessage;
      const displayTitle = session.title || "New Chat";
      const isEditing = this.state.editingSessionId === session.id;
      const isActive = this.props.location?.pathname?.endsWith(`/${session.id}`);

      return (
        <div
          key={session.id}
          className={`group relative flex items-center gap-2 px-3 py-2.5 rounded-lg transition-colors mb-2.5 ${
            isActive
              ? "bg-gradient-to-r from-primary-blue to-blue-400 shadow-md"
              : "bg-white/5 hover:bg-white/10"
          }`}
        >
          {isEditing ? (
            <div className="flex flex-col flex-grow gap-1">
              <input
                type="text"
                value={this.state.editingTitle}
                onChange={this.handleRenameChange}
                onKeyDown={(e) => this.handleRenameKeyDown(e, session.id)}
                onBlur={() => this.handleRenameSubmit(session.id)}
                autoFocus
                className="text-xs font-medium border border-white/20 bg-transparent rounded px-2 py-1 w-full focus:outline-none focus:border-white text-white placeholder:text-gray-400"
                placeholder="Enter chat title..."
              />
              <span className="text-[0.65rem] text-gray-300">
                Enter to save · Esc to cancel
              </span>
            </div>
          ) : (
            <button
              onClick={() => this.handleContinueChat(session.id)}
              className="flex flex-col flex-grow text-left min-w-0"
            >
              <span
                className={`text-sm font-medium truncate ${
                  isActive ? "text-white" : "text-gray-100"
                }`}
              >
                {displayTitle}
              </span>
              <span className="text-xs text-gray-300 truncate">{trimmedMessage}</span>
            </button>
          )}

          {!isEditing && (
            <button
              onClick={() => this.toggleMenu(session.id)}
              className="shrink-0 p-1 rounded-md text-gray-300 hover:text-white hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          )}

          {this.state.activeMenuId === session.id && (
            <div className="absolute right-2 top-9 bg-[#111827] border border-white/10 rounded-lg shadow-lg py-1 z-50 min-w-[140px]">
              <button
                onClick={() => this.handleStartRename(session.id, session.title)}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-100 hover:bg-white/5"
              >
                <Pencil className="w-3.5 h-3.5" />
                Rename
              </button>
              <button
                onClick={() => this.handleDeleteChatSession(session.id)}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
            </div>
          )}
        </div>
      );
    });
  }

  render() {
    const { isCollapsed } = this.props;
    const { isMobileView } = this.state;

    if (isCollapsed && !isMobileView) return null;

    return (
      <div className="group flex flex-col w-full md:w-[17rem] h-screen bg-gradient-to-b from-[#2d3b5f] to-[#1e2940] border-r border-white/10 overflow-hidden shadow-none">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-6 pb-4 shrink-0 border-b border-white/5">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="w-7 h-7 shrink-0 bg-white rounded-md flex items-center justify-center">
              <img src={CESSVG} alt="CES logo" className="h-5 w-5" />
            </div>
            <span className="font-SFPro font-semibold text-white text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 delay-75">
              CX2 Chat
            </span>
          </div>
          <button
            onClick={this.handleCollapseToggle}
            className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            title="Collapse sidebar"
          >
            <PanelLeft className="w-4 h-4" />
          </button>
        </div>

        {/* User info */}
        <div className="flex items-center gap-2.5 px-4 pt-3 pb-4 shrink-0 border-b border-white/5">
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">
            <User className="w-4 h-4 text-gray-200" />
          </div>
          <div className="flex flex-col overflow-hidden">
            <span className="text-sm font-medium text-white truncate">
              {this.props.user?.first_name} {this.props.user?.last_name}
            </span>
            <span className="text-[0.7rem] text-gray-300 truncate">
              Recent CX2 conversations
            </span>
          </div>
        </div>

        {/* New chat button */}
        <div className="px-4 pt-3 pb-4 shrink-0">
          <button
            onClick={this.handleStartNewChat}
            className="flex items-center gap-2 w-full px-3 py-2.5 bg-gradient-to-r from-primary-blue to-blue-400 text-white rounded-lg text-sm font-medium hover:shadow-md hover:brightness-105 transition-all"
          >
            <Plus className="w-4 h-4" />
            <span className="whitespace-nowrap">New Chat</span>
          </button>
        </div>

        {/* Sessions label */}
        <div className="px-4 pb-2 shrink-0">
          <span className="text-[0.7rem] font-semibold text-gray-300 uppercase tracking-wider opacity-80">
            Recent Chats
          </span>
        </div>

        {/* Chat sessions list */}
        <div className="flex-1 overflow-y-auto px-2 pb-4">
          {this.renderChatSessions()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  SideBarchatSessions: state.ai.SideBarchatSessions,
  SideBarchatSessionsFetchSuccess: state.ai.SideBarchatSessionsFetchSuccess,
  user: state.authentication.user,
  isCollapsed: state.interactions.isCollapsed,
});

const MemoizedAIChatBotSideBar = React.memo(AIChatBotSideBar, (prevProps, nextProps) => {
  return (
    prevProps.SideBarchatSessions === nextProps.SideBarchatSessions &&
    prevProps.SideBarchatSessionsFetchSuccess === nextProps.SideBarchatSessionsFetchSuccess &&
    prevProps.location.pathname === nextProps.location.pathname &&
    prevProps.isCollapsed === nextProps.isCollapsed
  );
});

export default withRouterHooks(
  connect(mapStateToProps, {
    FetchChatSessionsAction,
    FetchIndividualChatSessionAction,
    ContinueChatSessionAddSessionIDToReduxAction,
    resetContinueChatSessionAction,
    DeleteChatSessionAction,
    ResetIsNewChatSession,
    RenameChatSessionAction,
    CollapseCX2SidebarInactiveAction,
    CollapseCX2SidebarActiveAction,
  })(MemoizedAIChatBotSideBar)
);
